import { useState, useMemo } from "react";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { useSizing } from "@/lib/sizing-store";
import { Input } from "@/components/ui/input";
import { AssignOptionRow } from "../dialogs/AssignmentDialogShell";
import { BulkAssignmentShell } from "./BulkAssignmentShell";

/**
 * BulkImageDialog — attach a DAM library image to many products at once.
 * Search-enabled because merchants may have dozens of chart images. Applies
 * the same imageId to every target product (or skips when non-empty and
 * mode="empty-only").
 */
export const BulkImageDialog = ({ selectedProducts, onClose }) => {
    const { images, setProductSizingImage } = useSizing();
    const [query, setQuery] = useState("");
    const [saving, setSaving] = useState(false);
    const [mode, setMode] = useState("empty-only");

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return images;
        return images.filter((img) => (img.name || "").toLowerCase().includes(q));
    }, [images, query]);

    const conflicts = selectedProducts.filter((p) => !!p.sizingImageId);
    const conflictCount = conflicts.length;

    const apply = async (imgId) => {
        if (saving) return;
        const targets = mode === "empty-only"
            ? selectedProducts.filter((p) => !p.sizingImageId)
            : selectedProducts;
        if (targets.length === 0) {
            toast.info("Etkilenecek ürün yok");
            return;
        }
        setSaving(true);
        try {
            const snapshot = targets.map((p) => ({ id: p.id, prevImageId: p.sizingImageId || null }));
            targets.forEach((p) => setProductSizingImage(p.id, imgId));
            const label = images.find((i) => i.id === imgId)?.name || "Görsel";
            toast.success(`${targets.length} ürüne "${label}" görseli bağlandı`, {
                duration: 5000,
                action: {
                    label: "Geri Al",
                    onClick: () => {
                        snapshot.forEach((s) => setProductSizingImage(s.id, s.prevImageId));
                        toast.success(`${snapshot.length} ürünün görseli eski haline döndü`);
                    },
                },
            });
            onClose();
        } catch {
            toast.error("Toplu görsel güncellemesi başarısız");
        } finally {
            setSaving(false);
        }
    };

    return (
        <BulkAssignmentShell
            title="Toplu görsel ata"
            count={selectedProducts.length}
            conflictCount={conflictCount}
            mode={mode}
            onModeChange={setMode}
            onClose={onClose}
            testId="bulk-image-dialog"
        >
            {images.length > 0 && (
                <div className="sticky top-0 -mt-4 pt-3 pb-2 -mx-5 px-5 bg-muted z-10">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                        <Input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Görsel ara…"
                            className="h-9 pl-9 text-xs bg-background"
                            data-testid="bulk-image-dialog-search"
                        />
                    </div>
                </div>
            )}
            {images.length === 0 ? (
                <div className="text-center py-10 text-xs text-muted-foreground">Henüz görsel yok.</div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-8 text-xs text-muted-foreground">Sonuç yok</div>
            ) : (
                filtered.map((img) => (
                    <AssignOptionRow
                        key={img.id}
                        current={false}
                        onClick={() => apply(img.id)}
                        disabled={saving}
                        testId={`bulk-image-option-${img.id}`}
                    >
                        <div className="h-10 w-10 rounded-md border border-border bg-muted/40 overflow-hidden shrink-0 p-1">
                            <img
                                src={`${process.env.REACT_APP_BACKEND_URL}/api/embed/images/${img.id}`}
                                alt=""
                                className="block w-full h-full object-contain"
                            />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{img.name || "İsimsiz görsel"}</div>
                            {img.alt && (
                                <div className="text-[10.5px] text-muted-foreground truncate">{img.alt}</div>
                            )}
                        </div>
                    </AssignOptionRow>
                ))
            )}
        </BulkAssignmentShell>
    );
};
